import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
    makeStyles, Typography,
} from '@material-ui/core';
import { FetchPolicy, DataSort } from 'utils/enum/Core';
import { useQuery, useApolloClient } from '@apollo/client';
import ModalUtils from 'utils/ModalUtils';
import DateUtils from 'lib/DateUtils';
import ROLE from 'utils/enum/OpenAIEnum';
import AIStyle from 'styles/modules/ai/AIStyle';
import MessageAdd from 'components/widgets/sms/MessageAdd';
import AIQuery from 'services/graphQL/query/ai/AIQuery';
import AIMutation from 'services/graphQL/mutate/ai/AIMutation';

const AIConversationSpot = ({
    threadId,
    readOnly,
}) => {
    const client = useApolloClient();
    const useStyles = makeStyles((theme) => AIStyle.content(theme, true));
    const classes = useStyles();

    const [state, setState] = useState({
        conversation: [],
        sendingMessageInConversation: false,
        conversationUpdated: false,
        runId: null,
    });

    const {
        conversation,
        sendingMessageInConversation,
        conversationUpdated,
        runId,
    } = state;

    const {
        data: messagesData,
        loading: loadingMessages,
        error: errorLoadingMessages,
    } = useQuery(AIQuery.LIST_MESSAGES_IN_THREAD, {
        variables: {
            threadId,
            runId,
            order: DataSort.ASC,
        },
        skip: !threadId,
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    useEffect(() => {
        if (errorLoadingMessages) {
            ModalUtils.errorMessage(errorLoadingMessages?.graphQLErrors);
            return;
        }

        if (!loadingMessages) {
            const { messages = [] } = messagesData?.listMessagesInThread ?? {};
            setState((prevState) => ({
                ...prevState,
                sendingMessageInConversation: false,
                conversationUpdated: true,
                conversation: [
                    ...prevState.conversation,
                    ...(messages
                        .map(({ role, createdOn, content: { value } }) => ({ role, message: value, createdOn }))
                        .filter(({ message }) => !message.includes('Generate a performance report for our'))),
                ],
            }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingMessages, errorLoadingMessages]);

    useEffect(() => {
        const conversationParent = document.getElementById('conversation-parent');
        if (conversationParent) {
            const { children } = conversationParent;
            const total = children.length;

            if (total > 0) {
                children.item(total - 1).scrollIntoView({
                    behavior: 'smooth',
                    block: 'end',
                });
            }

            setState((prevState) => ({
                ...prevState,
                conversationUpdated: false,
            }));
        }
    }, [conversationUpdated]);

    const sendMessage = async (text) => {
        setState((prevState) => ({
            ...prevState,
            sendingMessageInConversation: true,
            conversationUpdated: true,
            conversation: [...prevState.conversation, { role: ROLE.USER, message: text?.trim(), createdOn: Date.now() / 1000 }],
        }));

        try {
            const { data: addMessageData } = await client.mutate({
                mutation: AIMutation.ADD_MESSAGE_IN_THREAD,
                variables: {
                    threadId,
                    role: ROLE.USER,
                    content: text,
                },
                fetchPolicy: FetchPolicy.NO_CACHE,
            });

            setState((prevState) => ({
                ...prevState,
                sendingMessageInConversation: false,
                runId: addMessageData?.addMessageInThread,
            }));
        } catch (error) {
            setState((prevState) => ({
                ...prevState,
                sendingMessageInConversation: false,
            }));

            ModalUtils.errorMessage(null, error.message);
        }
    };

    return (
        <div className={classes.container}>
            <div className={classes.label}>
                Conversation
            </div>
            <div className={clsx(classes.list, classes.conversation)}>
                <div id="conversation-parent">
                    {conversation.map(({
                        role,
                        message,
                        createdOn,
                    }, index) => {
                        const isUser = role === ROLE.USER;
                        return (
                            <div key={index} className={clsx(classes.main, { [classes.assistantMessageBox]: !isUser })}>
                                <div className={clsx(classes.message, isUser ? classes.userMessage : classes.assistantMessage)}>
                                    <Typography className={classes.text}>
                                        {message}
                                    </Typography>
                                    {createdOn != null && (
                                        <div className={classes.date}>
                                            {DateUtils.getFormattedDateInUserTimezone(Number(createdOn * 1000))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
                {threadId && !readOnly && (
                    <div>
                        <MessageAdd
                            disabled={sendingMessageInConversation}
                            onSubmit={
                                ({ text }) => sendMessage(text)
                            }
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

AIConversationSpot.defaultProps = {
    threadId: null,
    readOnly: false,
};

AIConversationSpot.propTypes = {
    threadId: PropTypes.string,
    readOnly: PropTypes.bool,
};

export default AIConversationSpot;
